import type { AxiosInstance } from 'axios';
import authService from './auth.service';
import type { BankTransaction } from './bankTransactions.service';

export interface ReconciliationSummary {
  pending: number;
  disputed: number;
  pending_oldest_age_days: number;
  pending_by_category: Record<string, number>;
  payouts_open: number;
  payouts_stale: number;
  payouts_stale_oldest_age_days: number;
  payouts_failed_or_returned: number;
  payout_stale_after_days: number;
}

export interface PendingEntry {
  ledger_entry_id: string;
  user_id: string;
  product_id: string | null;
  entry_type: 'credit' | 'debit';
  category: string;
  amount: string;
  currency_code: string;
  reference_number: string | null;
  reconciliation_status: 'pending' | 'disputed';
  created_at: string;
  age_days: number;
}

export interface SweepResult {
  deposits_reconciled: number;
  deposits_disputed: number;
  withdrawals_reconciled: number;
  internal_reconciled: number;
}

export interface StatementFormat {
  format_id: string;
  label: string;
  markets: string[];
}

export interface StatementImportResult {
  batch_id: string;
  format_id: string;
  inserted: number;
  duplicates: number;
  rejected: { line_no: number; reason: string }[];
  auto_matched_deposits: number;
  /** Credits matched on a client's permanent funding reference (self-service). */
  self_service_deposits: number;
  /** Credits that carried a funding reference but stayed in the manual queue. */
  self_service_refused: number;
  /** Matched deposits the platform accepted under the auto-acceptance rules. */
  auto_accepted_deposits: number;
  payouts_linked: number;
  /** Debits no payout claimed that were proposed as a funds operator transfer (a manager confirms). */
  debits_proposed: number;
  /** Closing balances this import recorded for the daily cash proof (0144). */
  balances_recorded?: number;
  /** Why a file that has a closing-balance column recorded none. */
  balance_note?: string | null;
}

export interface ExportParams {
  includeDisputed: boolean;
  /** YYYY-MM-DD, UTC; the server refuses a date in the future. Omitted = today. */
  asOf?: string;
}

export interface PendingExport {
  blob: Blob;
  filename: string;
  /** null when the response did not expose the header: the file cannot say. */
  truncated: boolean | null;
  pending: string | null;
  disputed: string | null;
}

/** The name the server chose (`reconciliation_pending_<date>.csv`), or a safe default. */
export function filenameFrom(disposition: string | null): string {
  const match = disposition ? /filename="?(\w[\w-]*\.csv)"?(?:;|$)/.exec(disposition) : null;
  return match ? match[1] : 'reconciliation_pending.csv';
}

/** Ledger <-> bank reconciliation report (`/api/v1/ops/reconciliation`) and the
 *  bank-statement CSV import that feeds it. */
class ReconciliationService {
  private get api(): AxiosInstance {
    return authService.getApi();
  }

  async summary(): Promise<ReconciliationSummary> {
    const res = await this.api.get<ReconciliationSummary>('/api/v1/ops/reconciliation/summary');
    return res.data;
  }

  async pending(limit = 200): Promise<PendingEntry[]> {
    const res = await this.api.get<{ items: PendingEntry[] }>(
      '/api/v1/ops/reconciliation/pending',
      { params: { limit } },
    );
    return res.data.items;
  }

  async run(): Promise<SweepResult> {
    const res = await this.api.post<SweepResult>('/api/v1/ops/reconciliation/run');
    return res.data;
  }

  async statementFormats(market: string): Promise<StatementFormat[]> {
    const res = await this.api.get<StatementFormat[]>(
      '/api/v1/ops/bank-transactions/statement-formats',
      { params: { market } },
    );
    return res.data;
  }

  async importStatement(
    concentrationAccountId: string, file: File, formatId = 'generic_csv',
  ): Promise<StatementImportResult> {
    const form = new FormData();
    form.append('concentration_account_id', concentrationAccountId);
    form.append('format', formatId);
    form.append('file', file, file.name);
    const res = await this.api.post<StatementImportResult>(
      '/api/v1/ops/bank-transactions/import-csv',
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return res.data;
  }

  /** Ask for an UNMATCHED captured line to be voided (5-500 characters of reason).
   *  Nothing moves: a manager other than the requester confirms it. A matched wire is
   *  refused with 409 `wire_matched`, a second request with `void_already_requested`. */
  async requestVoid(txId: string, reason: string): Promise<BankTransaction> {
    const res = await this.api.post<BankTransaction>(
      `/api/v1/ops/bank-transactions/${txId}/void`,
      { reason },
    );
    return res.data;
  }

  /** A manager other than the requester confirms the void with their current
   *  authenticator code. The line leaves the queue and the cash proof for good. */
  async confirmVoid(txId: string, mfaCode: string): Promise<BankTransaction> {
    const res = await this.api.post<BankTransaction>(
      `/api/v1/ops/bank-transactions/${txId}/void/confirm`,
      { mfa_code: mfaCode },
    );
    return res.data;
  }

  /** A manager refuses the void, or the requester takes it back (5-500 characters).
   *  The line stays in the queue. */
  async declineVoid(txId: string, note: string): Promise<BankTransaction> {
    const res = await this.api.post<BankTransaction>(
      `/api/v1/ops/bank-transactions/${txId}/void/decline`,
      { note },
    );
    return res.data;
  }

  /** The uncapped auditor export as a CSV file. Manager / super_admin only. The file
   *  is fetched through the authenticated instance as a blob; the response headers say
   *  how many rows are still pending and whether the server cut the file short. */
  async exportPendingCsv(params: ExportParams): Promise<PendingExport> {
    const res = await this.api.get<Blob>('/api/v1/ops/reconciliation/export', {
      params: {
        format: 'csv',
        include_disputed: params.includeDisputed,
        ...(params.asOf ? { as_of: params.asOf } : {}),
      },
      responseType: 'blob',
    });
    const header = (name: string): string | null => {
      const v = (res.headers as Record<string, unknown>)[name];
      return typeof v === 'string' ? v : null;
    };
    const truncated = header('x-reconciliation-truncated');
    return {
      blob: res.data,
      filename: filenameFrom(header('content-disposition')),
      truncated: truncated === null ? null : truncated === 'true',
      pending: header('x-reconciliation-pending'),
      disputed: header('x-reconciliation-disputed'),
    };
  }
}

export default new ReconciliationService();
